import type { User_Legacy } from '@racemap/utilities/types/users';
import { Modal } from 'antd';
import type { Immutable } from 'immer';
import { type FC, useEffect, useState } from 'react';
import { useCurrentEvent } from 'src/lib/customHooks/useCurrentEvent';
import { useCurrentUser } from 'src/lib/customHooks/useCurrentUser';
import { useUserCanActivateDirectly } from 'src/lib/customHooks/useUserCanActivateDirectly';
import { sendAnalyticsEvent } from 'src/lib/googleAnalytics';
import { toast } from 'src/lib/toast';
import type { CurrentEvent } from '../../../store/events/events_reducers';
import { useStore } from '../../../store/reducers';
import { useDoneView } from './useDoneView';
import { useEmbeddedStripeView } from './useEmbeddedStripeView';
import { type Checks, type ViewSlots, useOverviewView } from './useOverviewView';
import { ViewStates, checkPaymentStatus, initPayment } from './utils';

type Props = {
  show: boolean;
  onClose: () => void;
};

const MODAL_WIDTH_BY_VIEW: Partial<Record<ViewStates, number>> = {
  [ViewStates.OVERVIEW]: 800,
  [ViewStates.STRIPE]: 500,
  [ViewStates.DONE]: 520,
};

export const PaymentModal: FC<Props> = ({ show = false, onClose }) => {
  const currentUser = useCurrentUser();
  const currentEvent = useCurrentEvent();

  useEffect(() => {
    if (show) sendAnalyticsEvent('payment-modal-open');
  }, [show]);

  if (currentUser == null || currentEvent == null) return null;

  return (
    <PaymentModalContent
      show={show}
      onClose={onClose}
      currentUser={currentUser}
      currentEvent={currentEvent}
    />
  );
};

type ContentProps = {
  show: boolean;
  onClose: () => void;
  currentUser: Immutable<User_Legacy>;
  currentEvent: Immutable<CurrentEvent>;
};

const PaymentModalContent: FC<ContentProps> = ({ show, onClose, currentUser, currentEvent }) => {
  const { canActivateDirectly, isLoading } = useUserCanActivateDirectly(currentUser.id);

  const [viewState, setViewState] = useState<ViewStates | null>(null);
  const [checks, setChecks] = useState<Checks>({
    acceptTerms: false,
    acceptSubscription: false,
    canActivateDirectlyFetched: !isLoading && canActivateDirectly !== undefined,
  });
  const { loadCurrentEvent } = useStore((s) => s.events.actions);

  useEffect(() => {
    if (viewState != null) return;
    if (!show) return;

    resetPaymentState(currentEvent.id);
  }, [currentEvent, show]);

  useEffect(() => {
    setChecks((prev) => ({
      ...prev,
      canActivateDirectlyFetched: !isLoading && canActivateDirectly !== undefined,
    }));
  }, [isLoading, canActivateDirectly]);

  async function payment() {
    try {
      const paymentInfos = await initPayment(currentEvent);

      switch (paymentInfos.status) {
        case 'PAID':
          await moveToDone();
          break;
      }
    } catch (e) {
      if (e instanceof Error) {
        toast.error(e.message);
        console.error(e.message);
      }
    }
  }

  async function resetPaymentState(eventId: string) {
    const paymentState = await checkPaymentStatus(eventId);
    switch (paymentState.status) {
      case 'PAID':
        await moveToDone();
        break;
      case 'NONE':
        setViewState(ViewStates.OVERVIEW);
        break;
    }
  }

  function moveToStripe() {
    setViewState(ViewStates.STRIPE);
  }

  async function moveToDone() {
    setViewState(ViewStates.DONE);
    sendAnalyticsEvent('payment-done');
    await loadCurrentEvent(currentEvent.id);
  }

  const handleOverviewNext = async () => {
    sendAnalyticsEvent('payment-accept-terms');

    if (canActivateDirectly) {
      sendAnalyticsEvent('payment-activate-event');
      await payment();
      return;
    }

    moveToStripe();
  };

  const handleStripeNext = async () => {
    await moveToDone();
  };

  const overviewSlots = useOverviewView({
    onClose,
    onNext: handleOverviewNext,
    checks,
    onChecksChange: setChecks,
  });

  const stripeSlots = useEmbeddedStripeView({
    user: currentUser,
    event: currentEvent,
    enabled: viewState === ViewStates.STRIPE,
    onBack: () => setViewState(ViewStates.OVERVIEW),
    onCancel: onClose,
    onNext: handleStripeNext,
  });

  const doneSlots = useDoneView({ onClose });

  const getActiveSlots = (): ViewSlots | null => {
    switch (viewState) {
      case ViewStates.OVERVIEW:
        return overviewSlots;
      case ViewStates.STRIPE:
        return stripeSlots;
      case ViewStates.DONE:
        return doneSlots;
      default:
        return null;
    }
  };
  const activeSlots = getActiveSlots();

  return (
    <Modal
      open={show}
      onCancel={onClose}
      width={viewState != null ? MODAL_WIDTH_BY_VIEW[viewState] : undefined}
      title={activeSlots?.title}
      footer={activeSlots?.footer ?? null}
    >
      {activeSlots?.body}
    </Modal>
  );
};
